import { useCallback, useEffect, useState } from 'react';
import { Globe2, LayoutGrid, List, Send, Package, AlertTriangle, Receipt, FileDown, Camera } from 'lucide-react';
import { api } from '../lib/api';
import Modal from '../components/ui/Modal';
import LedgerTab from '../components/intercompany/LedgerTab';
import PaymentsTab from '../components/intercompany/PaymentsTab';
import StockTab from '../components/intercompany/StockTab';
import ClaimsTab from '../components/intercompany/ClaimsTab';
import BillsTab from '../components/intercompany/BillsTab';
import {
  Money, Field, TextInput, TextArea, Select, cardCls, btnCls, btn2Cls, fmtDate, today, eur, whoOwes, fetchStatement, STATUS_LABEL, usd,
} from '../components/intercompany/shared';

// Concria Oy: the money between CCS Surfaces and Concria Oy (Finland), both ways, with the
// running "who owes whom", agreed-net snapshots, wires, consignment stock at Pinnacle,
// refunds and claims, and vendor bills. All three partners see everything and can add;
// Chad marks items agreed, settled or written off. Nothing here sends anything to anyone.

const TABS = [
  { key: 'overview', label: 'Balance', icon: LayoutGrid },
  { key: 'ledger', label: 'Ledger', icon: List },
  { key: 'payments', label: 'Payments', icon: Send },
  { key: 'stock', label: 'Consignment stock', icon: Package },
  { key: 'claims', label: 'Refunds and claims', icon: AlertTriangle },
  { key: 'bills', label: 'Bills', icon: Receipt },
];

function readTab() {
  try { return new URLSearchParams(window.location.search).get('tab') || localStorage.getItem('concria_oy_tab') || 'overview'; } catch { return 'overview'; }
}

export default function ConcriaOyPage() {
  const [tab, setTabState] = useState(readTab);
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [claims, setClaims] = useState([]);
  const [error, setError] = useState('');
  const setTab = (t) => { setTabState(t); try { localStorage.setItem('concria_oy_tab', t); } catch { /* storage unavailable */ } };

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([api.get('/intercompany/summary'), api.get('/intercompany/claims')]);
      setSummary(s); setClaims(c.claims); setError('');
      api.get('/intercompany/books-accounts').then((r) => setAccounts(r.accounts)).catch(() => setAccounts([]));
      api.get('/intercompany/books-deals').then((r) => setDeals(r.deals)).catch(() => setDeals([]));
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error && !summary) return <div className="p-4 text-rose-600">{error}</div>;
  if (!summary) return <div className="p-4 text-[var(--text-secondary)]">Loading...</div>;
  const me = summary.me;
  // posting: is the Books posting layer on (LP-309), for the "In Books" chips.
  const props = { me, accounts, deals, claims, posting: summary.posting || null, onChanged: load };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--text-primary)]"><Globe2 size={20} /> Concria Oy</h1>
        <p className="text-sm text-[var(--text-secondary)]">
          What CCS and Concria Oy owe each other, line by line, with both sides' positions.
          {!me.can_approve && ' You can add and edit; Chad marks items agreed, settled or written off.'}
        </p>
      </div>
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-[var(--border)]">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${tab === key ? 'border-primary text-primary' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>
      {tab === 'overview' && <Overview summary={summary} me={me} onGo={setTab} onChanged={load} />}
      {tab === 'ledger' && <LedgerTab {...props} />}
      {tab === 'payments' && <PaymentsTab {...props} />}
      {tab === 'stock' && <StockTab {...props} />}
      {tab === 'claims' && <ClaimsTab {...props} />}
      {tab === 'bills' && <BillsTab {...props} />}
    </div>
  );
}

function Overview({ summary, me, onGo, onChanged }) {
  const { balance } = summary;
  const p = balance.positions;
  const [snaps, setSnaps] = useState(null);
  const [snapOpen, setSnapOpen] = useState(false);
  const [error, setError] = useState('');
  const loadSnaps = useCallback(() => api.get('/intercompany/snapshots').then((r) => setSnaps(r.snapshots)).catch((e) => setError(e.message)), []);
  useEffect(() => { loadSnaps(); }, [loadSnaps, summary]);

  const eurNote = (c) => (c ? `plus ${eur(c)} ${c > 0 ? 'owed by Concria' : 'owed by CCS'} not converted to USD` : null);
  const tile = (label, net, sub, extra) => (
    <div className={cardCls}>
      <div className="text-xs text-[var(--text-secondary)]">{label}</div>
      <div className="mt-1 text-xl font-semibold"><Money cents={net} /></div>
      <div className="text-xs text-[var(--text-secondary)]">{sub}</div>
      {extra && <div className="mt-1 text-xs text-amber-700 dark:text-amber-400">{extra}</div>}
    </div>
  );
  const statement = async (format, snapshotId) => { try { await fetchStatement({ format, snapshotId }); } catch (e) { setError(e.message); } };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tile('Agreed balance', p.agreed.net_cents, whoOwes(p.agreed.net_cents), eurNote(p.agreed.unconverted_eur_cents))}
        {tile('If every open item held', p.all_open.net_cents, whoOwes(p.all_open.net_cents), eurNote(p.all_open.unconverted_eur_cents))}
        {tile('Disputed', p.disputed.net_cents, `${p.disputed.count} ${p.disputed.count === 1 ? 'item' : 'items'}`, eurNote(p.disputed.unconverted_eur_cents))}
        {tile('Awaiting response', p.claimed.net_cents, `${p.claimed.count} ${p.claimed.count === 1 ? 'item' : 'items'}`, eurNote(p.claimed.unconverted_eur_cents))}
      </div>
      <p className="text-xs text-[var(--text-secondary)]">Positive = Concria Oy owes CCS. Payments are netted in: {usd(balance.payments.received_from_concria_cents)} received from Concria, {usd(balance.payments.paid_to_concria_cents)} paid to Concria{balance.payments.unallocated_cents ? `, ${usd(balance.payments.unallocated_cents)} not yet matched to a line` : ''}.</p>

      <div className={cardCls}>
        <h2 className="mb-2 font-semibold text-[var(--text-primary)]">By status</h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead><tr className="text-left text-xs text-[var(--text-secondary)]"><th className="py-1">Status</th><th className="py-1 text-right">Items</th><th className="py-1 text-right">Concria owes CCS</th><th className="py-1 text-right">CCS owes Concria</th><th className="py-1 text-right">Net</th></tr></thead>
            <tbody>
              {Object.entries(STATUS_LABEL).map(([k, label]) => {
                const b = balance.by_status[k];
                return (
                  <tr key={k} className="border-t border-[var(--border)]">
                    <td className="py-1.5"><button className="text-primary" onClick={() => onGo('ledger')}>{label}</button></td>
                    <td className="py-1.5 text-right">{b.count}</td>
                    <td className="py-1.5 text-right"><Money cents={b.concria_owes_cents} /></td>
                    <td className="py-1.5 text-right"><Money cents={b.ccs_owes_cents} />{b.unconverted_eur_cents ? <div className="text-xs text-amber-700 dark:text-amber-400">{eur(b.unconverted_eur_cents)}</div> : null}</td>
                    <td className="py-1.5 text-right font-medium"><Money cents={b.net_cents} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {balance.memo_only.count > 0 && <p className="mt-2 text-xs text-[var(--text-secondary)]">{balance.memo_only.count} reference-only {balance.memo_only.count === 1 ? 'line is' : 'lines are'} shown in the ledger but never counted (e.g. a combined re-bill of lines already listed).</p>}
      </div>

      <div className={cardCls}>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold text-[var(--text-primary)]">Statement and agreed-net snapshots</h2>
          <div className="flex flex-wrap gap-2">
            <button className={btn2Cls} onClick={() => statement('html')}><FileDown size={14} /> Printable statement</button>
            <button className={btn2Cls} onClick={() => statement('csv')}><FileDown size={14} /> Statement CSV</button>
            {me.can_approve && <button className={btnCls} onClick={() => setSnapOpen(true)}><Camera size={14} /> Record agreed net</button>}
          </div>
        </div>
        <p className="text-xs text-[var(--text-secondary)]">The statement is for the partners to review and send themselves; the platform never sends it. Internal notes are left off.</p>
        {error && <p className="mt-2 text-sm text-rose-600">{error}</p>}
        {snaps && snaps.length > 0 ? (
          <ul className="mt-3 divide-y divide-[var(--border)] text-sm">
            {snaps.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <div className="font-medium">{s.label}</div>
                  <div className="text-xs text-[var(--text-secondary)]">As of {fmtDate(s.as_of)} · {s.basis === 'agreed' ? 'agreed items' : 'all open items'} · <Money cents={s.net_cents} /> ({whoOwes(s.net_cents)}) · by {s.created_by}</div>
                  {s.note && <div className="text-xs text-[var(--text-secondary)]">{s.note}</div>}
                </div>
                <div className="flex gap-2">
                  <button className={btn2Cls} onClick={() => statement('html', s.id)}>Statement</button>
                  <button className={btn2Cls} onClick={() => statement('csv', s.id)}>CSV</button>
                </div>
              </li>
            ))}
          </ul>
        ) : <p className="mt-3 text-xs text-[var(--text-secondary)]">No snapshot yet. When both sides agree a net figure, Chad records it here so it is frozen with the lines behind it.</p>}
      </div>
      {snapOpen && <SnapshotModal onClose={() => setSnapOpen(false)} onSaved={() => { setSnapOpen(false); loadSnaps(); onChanged(); }} />}
    </div>
  );
}

function SnapshotModal({ onClose, onSaved }) {
  const [f, setF] = useState({ as_of: today(), basis: 'agreed', label: '' });
  const [error, setError] = useState('');
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try { await api.post('/intercompany/snapshots', Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''))); onSaved(); } catch (e) { setError(e.message); }
  };
  return (
    <Modal title="Record an agreed net" onClose={onClose}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Label" className="sm:col-span-2"><TextInput value={f.label} onChange={set('label')} placeholder="Agreed with Aki on the call" /></Field>
        <Field label="As of"><TextInput type="date" value={f.as_of} onChange={set('as_of')} /></Field>
        <Field label="Basis"><Select value={f.basis} onChange={set('basis')} options={{ agreed: 'Agreed items only', all_open: 'All open items' }} /></Field>
        <Field label="Note" className="sm:col-span-2"><TextArea value={f.note} onChange={set('note')} rows={2} /></Field>
      </div>
      <p className="mt-2 text-xs text-[var(--text-secondary)]">Freezes today's lines and payments with the net, so the figure can be shown later exactly as agreed.</p>
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      <button className={`${btnCls} mt-4`} onClick={save} disabled={!f.label}>Record</button>
    </Modal>
  );
}
